import React from 'react';
import { BaseAlert } from 'components/@common/BaseAlert';

export enum AlertVariant {
  SUCCESS = 'success',
  INFO = 'info',
  ERROR = 'error',
  WARNING = 'warning'
}

export enum AlertStyleVariant {
  FILLED = 'filled',
  OUTLINED = 'outlined',
  STANDARD = 'standard'
}

type Verticle = 'top' | 'bottom';
type Horizontal = 'center' | 'left' | 'right';
interface DefaultAlertOptions {
  duration?: number | null;
  location?: { vertical: Verticle; horizontal: Horizontal };
  classes?: { titleClass?: string; messageClass?: string; iconClass?: string };
}
export type OpenAlertPayload = {
  open?: boolean;
  title?: string | null;
  body: JSX.Element | string | null;
  variant: AlertVariant.SUCCESS | AlertVariant.INFO | AlertVariant.ERROR | AlertVariant.WARNING;
  styleVariant?: AlertStyleVariant.FILLED | AlertStyleVariant.OUTLINED | AlertStyleVariant.STANDARD;
  options?: DefaultAlertOptions;
  onClose?: VoidFunction | undefined;
  forceClose?: boolean;
  unmountOnClose?: boolean;
};
export type UseAlertState = OpenAlertPayload;

type Action = { type: 'OPEN_ALERT'; payload: OpenAlertPayload } | { type: 'CLOSE_ALERT' };

export type UseAlertDispatch = {
  openAlert: (payload: OpenAlertPayload) => void;
  closeAlert: () => void;
};

type UseAlert = [UseAlertState, UseAlertDispatch];

const getDefaultAlertOptions = (): DefaultAlertOptions => ({
  duration: 3000,
  location: { vertical: 'top', horizontal: 'center' }
});

export const getDefaultAlertPayload = (): OpenAlertPayload => ({
  variant: AlertVariant.SUCCESS,
  styleVariant: AlertStyleVariant.FILLED,
  open: false,
  title: null,
  body: null,
  unmountOnClose: true,
  options: getDefaultAlertOptions(),
  forceClose: false
});

const resolvePayload = (props: OpenAlertPayload) => {
  const { options } = props;
  const alertPayload = { ...getDefaultAlertPayload(), ...props };
  const updatedOptions = { ...getDefaultAlertOptions(), ...options };
  return { ...alertPayload, options: updatedOptions };
};

const alertReducer = (state: UseAlertState, action: Action): UseAlertState => {
  switch (action.type) {
    case 'OPEN_ALERT':
      return {
        ...state,
        ...resolvePayload(action.payload),
        unmountOnClose: true,
        open: true
      };
    case 'CLOSE_ALERT':
      return {
        ...state,
        open: false,
        unmountOnClose: true
      };
    default:
      return {
        ...state,
        open: false,
        unmountOnClose: true
      };
  }
};

const AlertContext = React.createContext<any>(getDefaultAlertPayload());

export const AlertProvider = props => {
  const [state, dispatch] = React.useReducer<React.Reducer<UseAlertState, Action>>(
    alertReducer,
    getDefaultAlertPayload()
  );

  const openAlert = (payload: OpenAlertPayload) => {
    dispatch({ type: 'OPEN_ALERT', payload });
  };

  const closeAlert = () => {
    if (state.onClose) state.onClose();
    dispatch({ type: 'CLOSE_ALERT' });
  };

  return (
    <AlertContext.Provider value={[state, { openAlert, closeAlert }]}>
      <BaseAlert {...state} onClose={closeAlert} />
      {props.children}
    </AlertContext.Provider>
  );
};

export const useAlert = (): UseAlert => {
  const [state, dispatch] = React.useContext<UseAlert>(AlertContext);
  return [state, dispatch];
};
